/*eslint max-lines-per-function: ["error", 206]*/
import React, { useCallback } from 'react';
import { Button, Card, Tab } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { SurveySearch } from '../../components/Surveys/SurveySearch';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { GridGetterFunction } from '../../../common/components/Grid/GridDataTypes';
import { useNavigation } from '../../../common/navigation';
import { TabsHistory } from '../../../app/components/TabsHistory';

enum SurveysTabs {
  MY_DRAFT = 'my-draft',
  MY = 'my',
  ACTIVE = 'active',
  ALL = 'all',
}

export const SurveysView: React.FC = () => {
  const pok = usePokCore();
  const nav = useNavigation();
  const { t, tk } = useTranslation('administrationLng');
  const lang = pok.getLanguage();

  const getData = useCallback(
    ((
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.surveys.getAllByPortion(
        t,
        tk,
        lang,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      )) as GridGetterFunction,
    [lang, pok.surveys, t, tk],
  );

  const getMyDraftData = useCallback(
    ((
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.surveys.getAllByPortion(
        t,
        tk,
        lang,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        'true', // onlyMy parameter
        'true', // onlyDraft parameter
      )) as GridGetterFunction,
    [lang, pok.surveys, t, tk],
  );

  const getMyData = useCallback(
    ((
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.surveys.getAllByPortion(
        t,
        tk,
        lang,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        'true', // onlyMy parameter
      )) as GridGetterFunction,
    [lang, pok.surveys, t, tk],
  );

  const getActiveData = useCallback(
    ((
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.surveys.getAllByPortion(
        t,
        tk,
        lang,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
        undefined, // onlyMy parameter
        undefined, // onlyDraft parameter
        'true', // onlyActive parameter
      )) as GridGetterFunction,
    [lang, pok.surveys, t, tk],
  );

  const getCount = useCallback(
    (filterText?: string, filterColumns?: string) =>
      pok.surveys.getAllCount(filterText, filterColumns),
    [pok.surveys],
  );

  const getMyDraftCount = useCallback(
    (filterText?: string, filterColumns?: string) =>
      pok.surveys.getAllCount(filterText, filterColumns, 'true', 'true'),
    [pok.surveys],
  );

  const getMyCount = useCallback(
    (filterText?: string, filterColumns?: string) =>
      pok.surveys.getAllCount(filterText, filterColumns, 'true'),
    [pok.surveys],
  );

  const getMyActiveCount = useCallback(
    (filterText?: string, filterColumns?: string) =>
      pok.surveys.getAllCount(
        filterText,
        filterColumns,
        undefined,
        undefined,
        'true',
      ),
    [pok.surveys],
  );

  const handleAdd = () => {
    nav.surveyEditor();
  };

  const handleRowClick = (id: string) => {
    nav.surveyEditor(id);
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">{t(tk.survey.surveys)}</Card.Title>
        <Button className="mb-4" variant="outline-primary" onClick={handleAdd}>
          {t(tk.survey.surveyNew)}
        </Button>
        <TabsHistory
          activeKey={SurveysTabs.MY_DRAFT}
          id="surveys"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey={SurveysTabs.MY_DRAFT}
            title={t(tk.survey.myDraftSurveys)}
            className="m-3"
          >
            <SurveySearch
              getData={getMyDraftData}
              getCount={getMyDraftCount}
              onRowClick={handleRowClick}
            />
          </Tab>
          <Tab
            eventKey={SurveysTabs.MY}
            title={t(tk.survey.mySurveys)}
            className="m-3"
          >
            <SurveySearch
              getData={getMyData}
              getCount={getMyCount}
              onRowClick={handleRowClick}
            />
          </Tab>
          <Tab
            eventKey={SurveysTabs.ACTIVE}
            title={t(tk.survey.activeSurveys)}
            className="m-3"
          >
            <SurveySearch
              getData={getActiveData}
              getCount={getMyActiveCount}
              onRowClick={handleRowClick}
            />
          </Tab>
          <Tab
            eventKey={SurveysTabs.ALL}
            title={t(tk.memo.all)}
            className="m-3"
          >
            <SurveySearch
              getData={getData}
              getCount={getCount}
              onRowClick={handleRowClick}
            />
          </Tab>
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
